import { useEffect, useState } from 'react';
import { getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Props {
  /** Server-relative path from the photo record (e.g. /api/v1/construction-photos/file/...). May be null. */
  src: string | null | undefined;
  alt?: string;
  className?: string;
  /** Fallback element rendered while loading or on error. */
  fallback?: React.ReactNode;
  onClick?: () => void;
  loading?: 'lazy' | 'eager';
}

// The construction-photos /file/* endpoint requires a JWT in the Authorization
// header, so a plain <img src> would 401. This component fetches the bytes,
// converts to a blob URL, and renders <img> against that.
export function AuthImg({ src, alt = '', className, fallback, onClick, loading = 'lazy' }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!src) { setUrl(null); setErrored(false); return; }
    setErrored(false);
    let cancelled = false;
    let objUrl: string | null = null;

    const jwt = getStoredToken();
    const fullUrl = src.startsWith('http') ? src : `${API_BASE}${src}`;
    fetch(fullUrl, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setUrl(objUrl);
      })
      .catch(() => { if (!cancelled) setErrored(true); });

    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [src]);

  if (!src || errored || !url) {
    return <>{fallback ?? <div className={className} aria-label={alt} />}</>;
  }
  return <img src={url} alt={alt} className={className} loading={loading} onClick={onClick} />;
}
